import { useSeo } from '../lib/seo';
/**
 * Itinerary builder — pick attractions, drag to reorder, save → get share URL.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, Copy, GripVertical, MapPin, Plus, Share2, Sparkles, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import NewsletterSignup from '../components/NewsletterSignup';
import { track } from '../lib/tracker';

function SortableStop({ a, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: a.slug });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-charcoal/10 rounded-3xl overflow-hidden flex items-center gap-4 p-3 shadow-soft" data-testid={`itinerary-stop-${a.slug}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-3 text-charcoal/40 hover:text-charcoal touch-none" aria-label="drag handle">
        <GripVertical size={18}/>
      </button>
      <img src={a.hero_image} alt={a.name} className="w-24 h-20 object-cover rounded-2xl shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg leading-tight truncate">{a.name}</div>
        <div className="text-muted text-sm flex items-center gap-1.5"><MapPin size={12}/> {a.town || 'Leicestershire'}</div>
      </div>
      <button onClick={() => onRemove(a.slug)} className="p-2 text-heritage hover:bg-heritage/5 rounded-lg shrink-0" aria-label="remove" data-testid={`itinerary-remove-${a.slug}`}>
        <Trash2 size={16}/>
      </button>
    </div>
  );
}

export default function ItineraryBuilder() {
  useSeo(
    'Build Your Leicestershire Itinerary | Go Leicestershire',
    'Hand-pick venues and build a shareable Leicestershire itinerary in minutes.'
  );
  const [all, setAll] = useState([]);
  const [picked, setPicked] = useState([]); // ordered list of attraction objects
  const [search, setSearch] = useState('');
  const [title, setTitle] = useState('My Leicestershire day');
  const [share, setShare] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/attractions?limit=120').then((r) => setAll(r.data.items));
  }, []);

  const filtered = useMemo(() => {
    if (!search) return all.slice(0, 30);
    const s = search.toLowerCase();
    return all.filter((a) => a.name.toLowerCase().includes(s) || (a.town || '').toLowerCase().includes(s)).slice(0, 30);
  }, [all, search]);

  const pickedSlugs = new Set(picked.map((p) => p.slug));

  function add(a) {
    if (pickedSlugs.has(a.slug)) return;
    setPicked([...picked, a]);
  }
  function remove(slug) { setPicked(picked.filter((p) => p.slug !== slug)); }

  function onDragEnd(e) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = picked.findIndex((p) => p.slug === active.id);
    const newIdx = picked.findIndex((p) => p.slug === over.id);
    setPicked(arrayMove(picked, oldIdx, newIdx));
  }

  async function save() {
    if (picked.length === 0) return;
    setSaving(true);
    try {
      const r = await api.post('/itineraries', { title, attraction_slugs: picked.map((p) => p.slug) });
      track.itineraryBuild(picked.length);
      const url = `${window.location.origin}/itinerary/${r.data.share_token}`;
      setShare({ url, token: r.data.share_token });
    } finally { setSaving(false); }
  }

  function copy() {
    navigator.clipboard.writeText(share.url);
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
      <div className="label text-heritage mb-3 inline-flex items-center gap-2"><Sparkles size={14}/> Itinerary builder</div>
      <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] mb-12">Build a day. <em className="not-italic text-heritage">Share it.</em></h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT: picker */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-charcoal/10 rounded-3xl p-6 sticky top-28">
            <div className="label text-charcoal/50 mb-3">Pick stops</div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search attractions..."
              data-testid="itinerary-search"
              className="w-full bg-base border border-charcoal/15 rounded-full px-5 py-3 mb-4 focus:outline-none focus:border-forest"
            />
            <div className="max-h-[520px] overflow-y-auto space-y-2 pr-1 scrollbar-hide">
              {filtered.map((a) => {
                const taken = pickedSlugs.has(a.slug);
                return (
                  <button
                    key={a.id}
                    onClick={() => add(a)}
                    disabled={taken}
                    data-testid={`itinerary-add-${a.slug}`}
                    className={`w-full flex items-center gap-3 p-2 rounded-2xl text-left transition-colors ${taken ? 'opacity-50' : 'hover:bg-stone/60'}`}
                  >
                    <img src={a.hero_image} alt={a.name} className="w-14 h-14 object-cover rounded-xl shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{a.name}</div>
                      <div className="text-xs text-muted truncate">{a.town || 'Leicestershire'} · {a.category?.replace(/_/g, ' ')}</div>
                    </div>
                    {taken ? <Check size={16} className="text-forest shrink-0"/> : <Plus size={16} className="text-charcoal/40 shrink-0"/>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT: itinerary */}
        <div className="lg:col-span-7">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-transparent font-display text-3xl lg:text-4xl mb-6 px-2 py-2 border-b-2 border-transparent focus:border-heritage focus:outline-none"
            data-testid="itinerary-title"
          />
          {picked.length === 0 ? (
            <div className="bg-stone/40 border-2 border-dashed border-charcoal/15 rounded-3xl p-16 text-center text-muted">
              <div className="font-display text-2xl mb-2">Empty day so far.</div>
              <div>Pick attractions from the left to build your itinerary.</div>
            </div>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={picked.map((p) => p.slug)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {picked.map((a) => <SortableStop key={a.slug} a={a} onRemove={remove} />)}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {picked.length > 0 && (
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={save} disabled={saving} data-testid="itinerary-save" className="bg-heritage text-base px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-heritage-deep disabled:opacity-50">
                <Share2 size={16}/> {saving ? 'Saving...' : 'Save & get share link'}
              </button>
              <button onClick={() => setPicked([])} className="text-muted hover:text-heritage text-sm font-semibold inline-flex items-center gap-1">
                <X size={14}/> Clear
              </button>
            </div>
          )}

          {share && (
            <>
              <div className="mt-8 bg-charcoal text-base rounded-3xl p-8" data-testid="itinerary-share-link">
                <div className="label text-mustard mb-3">Your day is saved</div>
                <h3 className="font-display text-2xl mb-4">Share this link</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input value={share.url} readOnly className="flex-1 bg-base/10 border border-base/20 rounded-full px-5 py-3 text-base/90 font-mono text-sm" data-testid="itinerary-share-url"/>
                  <button onClick={copy} className="bg-mustard text-charcoal font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:bg-base" data-testid="itinerary-copy">
                    <Copy size={14}/> Copy
                  </button>
                  <Link to={`/itinerary/${share.token}`} className="border border-base/30 px-6 py-3 rounded-full font-semibold text-center hover:border-mustard">View</Link>
                </div>
              </div>

              {/* Contextual signup right after the save — they're invested. */}
              <div className="mt-6 bg-stone/60 border border-charcoal/10 rounded-3xl p-7 md:p-8" data-testid="itinerary-email-signup">
                <div className="label text-heritage mb-2">Keep your day safe</div>
                <h3 className="font-display text-xl md:text-2xl mb-2">Want this emailed to you?</h3>
                <p className="text-charcoal/80 mb-4 text-sm md:text-base">
                  Add your email — we&apos;ll send the link plus our weekly digest. Subscribers go into our quarterly StayPlayExplore prize draw. One click to unsubscribe.
                </p>
                <NewsletterSignup variant="inline" source="itinerary-builder" buttonLabel="Email me the link" successMessage="Sent!" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
